/**
 * AI suspension confirm: local switch + required reason (TMMPDM-0008). TMMPDM-0088 (TMMAPI-0152):
 * the suspended state reads "AI suspended by the platform"; the action is unchanged.
 */
import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { SubmitButton } from '@/components/page';
import { adminApi } from '@/api/tmm/admin';
import { AppSwitch } from '@/components/ui/AppSwitch';
import { ApiError, errorCopy, isValidationFailed, validationFields } from '@/api/errors';

interface AiSuspensionDialogProps {
  open: boolean;
  slug: string;
  subscriptionId: number;
  suspended: boolean;
  onSuspendedChange: (suspended: boolean) => void;
  onClose: () => void;
  onApplied: (suspended: boolean) => void;
}

export function AiSuspensionDialog({
  open,
  slug,
  subscriptionId,
  suspended,
  onSuspendedChange,
  onClose,
  onApplied,
}: AiSuspensionDialogProps) {
  const { t } = useCopy();
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('subscriptions.ai.title', 'AI suspension')}</DialogTitle>
      {open ? (
        <AiSuspensionForm
          slug={slug}
          subscriptionId={subscriptionId}
          suspended={suspended}
          onSuspendedChange={onSuspendedChange}
          onClose={onClose}
          onApplied={onApplied}
        />
      ) : null}
    </Dialog>
  );
}

function AiSuspensionForm({
  slug,
  subscriptionId,
  suspended,
  onSuspendedChange,
  onClose,
  onApplied,
}: Omit<AiSuspensionDialogProps, 'open'>) {
  const { t } = useCopy();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);

  const submit = async () => {
    const trimmed = reason.trim();
    if (!trimmed) {
      setFieldError(t('subscriptions.ai.reasonRequired', 'Reason is required.'));
      return;
    }
    setSubmitting(true);
    setError(null);
    setFieldError(null);
    try {
      const response = await adminApi.setAiSuspension(slug, subscriptionId, {
        suspended,
        reason: trimmed,
      });
      onSuspendedChange(response.suspended);
      onApplied(response.suspended);
    } catch (err: unknown) {
      if (err instanceof ApiError && isValidationFailed(err)) {
        const fields = validationFields(err, t);
        setFieldError(fields.reason ?? errorCopy(err, t, ''));
      } else {
        setError(
          errorCopy(err, t, t('subscriptions.ai.error', "Couldn't change the AI suspension.")),
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <AppSwitch
            label={t('subscriptions.ai.suspended', 'AI suspended by the platform')}
            checked={suspended}
            onChange={onSuspendedChange}
          />
          <TextField
            label={t('subscriptions.ai.reason', 'Reason')}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            error={Boolean(fieldError)}
            helperText={fieldError}
            required
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('subscriptions.subscribeDialog.cancel', 'Cancel')}</Button>
        <SubmitButton onClick={() => void submit()} busy={submitting}>
          {t('subscriptions.ai.confirm', 'Confirm')}
        </SubmitButton>
      </DialogActions>
    </>
  );
}
